import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import API_BASE_URL from "../../config";
import Card from "../../Components/Card/Card";
import SEO from "../../Components/SEO/SEO.jsx";
import Loader from "../../Components/Loader/Loader";
import { categoriesService } from "../../Modules/Services/categories.service.ts";
import "./Category.css";
import { ChevronLeft, Search, UtensilsCrossed } from "lucide-react";

function toNiceTitle(value) {
  const s = String(value || "").trim();
  if (!s) return "";
  const lowered = s.toLocaleLowerCase("ru-RU");
  return lowered
    .split(/\s+/g)
    .map((w) => (w ? w[0].toLocaleUpperCase("ru-RU") + w.slice(1) : ""))
    .join(" ");
}

const CategoryPage = () => {
  const { categoryId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");

  useEffect(() => {
    const run = async () => {
      setLoading(true);
      try {
        const res = await categoriesService.getCategoryProducts(categoryId);
        setData(res);
      } catch (e) {
        setData(null);
      } finally {
        setLoading(false);
      }
    };
    run();
  }, [categoryId]);

  const title = useMemo(() => {
    if (!data?.category_name) return "Категория";
    return toNiceTitle(data.category_name);
  }, [data]);

  const products = data?.products || [];

  const filtered = useMemo(() => {
    const q = query.trim().toLocaleLowerCase("ru-RU");
    if (!q) return products;
    return products.filter((p) => {
      const name = String(p.product_name || "").toLocaleLowerCase("ru-RU");
      const comp = String(p.composition || "").toLocaleLowerCase("ru-RU");
      return name.includes(q) || comp.includes(q);
    });
  }, [products, query]);

  if (loading) return <Loader />;

  if (!data) {
    return (
      <div className="Category">
        <SEO title="Категория не найдена" />
        <div className="Category--Header">
          <button
            type="button"
            className="Category--Back"
            onClick={() => navigate("/")}
          >
            <ChevronLeft size={18} /> На главную
          </button>
        </div>
        <div className="Category--Empty">
          <UtensilsCrossed size={36} />
          <h2>Категория не найдена</h2>
          <p>Возможно, она была переименована или удалена.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="Category fade-in">
      <SEO title={`${title} — Шахризада`} />

      <div className="Category--Header">
        <button
          type="button"
          className="Category--Back"
          onClick={() => navigate("/")}
        >
          <ChevronLeft size={18} />
          <span>Назад</span>
        </button>
        <div className="Category--TitleBlock">
          <span className="section-eyebrow">Меню</span>
          <h1 className="Category--Title">{title}</h1>
          <span className="Category--Count">
            {products.length}{" "}
            {products.length === 1
              ? "позиция"
              : products.length >= 2 && products.length <= 4
              ? "позиции"
              : "позиций"}
          </span>
        </div>
      </div>

      {products.length > 4 && (
        <label className="Category--Search">
          <Search size={18} />
          <input
            type="search"
            placeholder="Найти блюдо..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </label>
      )}

      {filtered.length === 0 ? (
        <div className="Category--Empty">
          <UtensilsCrossed size={36} />
          <h2>Ничего не нашлось</h2>
          <p>Попробуйте изменить запрос или вернитесь к полному списку.</p>
        </div>
      ) : (
        <div className="Category--Grid">
          {filtered.map((product, index) => (
            <div
              key={product.product_id}
              className="Category--CardWrap fade-up"
              style={{ animationDelay: `${index * 35}ms` }}
            >
              <Card
                product={product}
                title={product.product_name}
                description={product.composition}
                variants={product.variants}
                image={`${API_BASE_URL}${product.image_url}`}
                compact
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default CategoryPage;
